import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Button } from '~/components/Button';
import { Icon } from '~/components/Icon';
import { IconButton } from '~/components/IconButton';
import { Input } from '~/components/Input/Input';
import { useAppliedFilters } from '~settings/components/FilterList/hooks/useAppliedFilters.tsx';
import { DEFAULT_SEARCH_LIMIT } from '~settings/constants';

import { Badge } from '../Badge';

interface ToolbarProps {
  onFilterClick?: () => void;
  placeholder: string;
  rightAddon?: ReactNode;
  setPageNumber?: (pageNumber: number) => void;
}

export function Toolbar({
  onFilterClick,
  placeholder,
  rightAddon,
  setPageNumber,
}: ToolbarProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get('search') || '';
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const { t } = useTranslation('settings');

  const { filters, isLoading } = useAppliedFilters();
  const chipCount = isLoading ? 0 : filters?.length || 0;

  const handleSearch = (value: string) => {
    if (value !== searchTerm) {
      if (value) {
        searchParams.set('search', value);
      } else {
        searchParams.delete('search');
      }

      setSearchParams(searchParams);

      setPageNumber?.(1);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setLocalSearchTerm(value);

    // Trigger search when input is cleared (via keyboard or clear button)
    if (value === '' && searchTerm) {
      handleSearch('');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearch(localSearchTerm);
    }
  };

  const handleClear = () => {
    setLocalSearchTerm('');
    searchParams.delete('search');
    setSearchParams(searchParams);
    setPageNumber?.(1);
  };

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  return (
    <div className="flex gap-md py-lg">
      {onFilterClick ? (
        <Button
          onClick={onFilterClick}
          variant="tertiary"
          size="large"
          className="relative rounded-sm border px-md bg-level-03 border-emphasis-subtle hover:bg-level-04"
          aria-label="open filter drawer"
        >
          {chipCount > 0 ? <Badge>{chipCount}</Badge> : null}
          <Icon
            type="sliders-simple"
            variant="solid"
            size="md"
            aria-label={t('filterButton')}
          />
        </Button>
      ) : null}

      <div className="group relative w-full">
        <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-sm">
          <Icon
            type="search"
            size="md"
            className="flex items-center justify-center p-sm text-subtle group-hover:text-default"
          />
        </div>
        <Input
          placeholder={placeholder}
          type="search"
          aria-controls="tool-bar-search"
          value={localSearchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          rounded="small"
          className="border px-2xl !outline-none bg-level-03 border-emphasis-subtle placeholder:text-subtle hover:bg-level-04 group-hover:placeholder:text-default [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
          maxLength={DEFAULT_SEARCH_LIMIT}
        />
        {localSearchTerm.length > 0 ? (
          <IconButton
            iconType="xmark"
            size="small"
            onClick={handleClear}
            className="absolute end-0 top-1/2 me-sm -translate-y-1/2 text-subtle hover:bg-transparent hover:text-default"
            aria-label="Clear search"
          />
        ) : null}
      </div>
      {rightAddon}
    </div>
  );
}

Toolbar.displayName = 'Toolbar';
